<template>
  <header>
    <!-- Standard navbar otherwise -->
    <!-- Marks AO's own chrome for the platform e2e suite, which asserts none of it renders inside the
         One Retail shell. Standalone only: App.vue drops this whole component when embedded. -->
    <v-app-bar
      data-rtls-product-chrome
      class="assortment-toolbar elevation-6"
      :class="{ warning: notUsingLocalDatabase }"
      :color="`#${backgroundColor}`"
    >
      <v-toolbar-items>
        <div v-if="hasBrandLogoEnabled" class="brand-logo__container">
          <v-btn exact variant="text" to="/" class="font-weight-bold home-link" :ripple="false">
            <v-img
              id="client-logo"
              cover
              class="brand-logo__img"
              src="/organisation-assets/brand-logo.png"
              :alt="$t('general.logo')"
              width="6rem"
            />
          </v-btn>
        </div>
        <v-btn exact variant="text" to="/" class="text-subtitle-1 font-weight-bold home-link">
          <BaseHomeButton />
        </v-btn>
      </v-toolbar-items>
      <v-spacer />
      <v-toolbar-items v-for="(route, index) in routes" :key="index">
        <v-btn
          v-if="!route.children || route.omitChildrenFromNavbar"
          :key="index"
          :name="route.linkText"
          :data-id-e2e="'nav-' + route.linkText"
          exact
          variant="text"
          :to="route.path"
          class="text-none text-subtitle-1 nav-link"
          :class="{ 'active-link': getActiveRoute(route) }"
        >
          {{ $t(route.linkText) }}
        </v-btn>
        <v-menu v-if="route.children && !route.omitChildrenFromNavbar">
          <template #activator="{ props }">
            <v-btn
              v-bind="props"
              :key="index"
              :name="route.linkText"
              :data-id-e2e="'nav-' + route.linkText"
              exact
              variant="text"
              class="text-none text-subtitle-1 nav-link"
              :class="$route.fullPath.includes(route.path) ? 'active-link' : ''"
            >
              {{ $t(route.linkText) }}
            </v-btn>
          </template>
          <v-list v-if="route.children">
            <v-list-item
              v-for="(item, idx) in route.children"
              :key="idx"
              :name="item.linkText"
              :data-id-e2e="'nav-' + item.linkText"
              :to="route.path + '/' + item.path"
              dark
            >
              <v-list-item-title>{{ $t(item.linkText) }}</v-list-item-title>
            </v-list-item>
          </v-list>
        </v-menu>
      </v-toolbar-items>
      <v-spacer />
      <v-toolbar-items>
        <v-menu
          v-if="isCogAvailable"
          open-on-click
          :close-on-content-click="false"
          location="bottom"
          max-width="170px"
        >
          <template #activator="{ props }">
            <img v-bind="props" class="cog-icon" :src="cog" />
          </template>
          <v-list class="nav-menu-list-item">
            <v-list-item v-if="hasTenantsSwitcherEnabled">
              <TenantSelector />
            </v-list-item>
            <v-list-item v-if="hasLocaleSwitcherEnabled">
              <LocaleSwitcher />
            </v-list-item>
            <v-list-item v-if="hasFormatsSwitcherEnabled">
              <DateLocaleSwitcher />
            </v-list-item>
            <v-list-item v-if="hasFormatsSwitcherEnabled">
              <LocaleSwitcherNumeric />
            </v-list-item>
          </v-list>
        </v-menu>
      </v-toolbar-items>
      <v-toolbar-items>
        <v-btn
          id="navbar-logout"
          variant="text"
          class="text-none text-subtitle-1 nav-link"
          @click="onLogout()"
        >
          <span>{{ $t('actions.logout') }}</span>
        </v-btn>
      </v-toolbar-items>
      <v-toolbar-items>
        <UserNotifications />
      </v-toolbar-items>
      <v-toolbar-items v-if="docsButtonVisible">
        <v-btn
          id="navbar-docs"
          class="text-none text-subtitle-1 nav-link"
          variant="text"
          size="large"
          color="primary"
          href="/api/docs/"
          target="_blank"
        >
          <v-icon primary size="28"> mdi-help-circle-outline </v-icon>
        </v-btn>
      </v-toolbar-items>
    </v-app-bar>
  </header>
</template>

<script>
import { get } from 'lodash';
import { mapActions, mapState } from 'pinia';
import { useContextStore } from '@/stores/context';
import cog from '@/assets/nav_cog.svg';
import { usePermission } from '@/composables/use-permission';

export default {
  setup() {
    const { canViewDocs } = usePermission();

    return {
      canViewDocs,
      sidebar: false,
      workpackageTruncationLength: 53,
      cog,
    };
  },

  computed: {
    ...mapState(useContextStore, [
      'hasPermission',
      'getCurrentTenant',
      'getClientConfig',
      'getShowAdminControlsPermissions',
      'getShowClientAdminControlsPermissions',
      'getShowReportingControlsPermissions',
      'getShowCompoundExtractsPermissions',
      'getShowReportingPowerBIPermissions',
      'getShowTemplatesPermissions',
      'getShowBundlesPermissions',
      'notUsingLocalDatabase',
      'getAvailableTenants',
    ]),

    backgroundColor() {
      return get(this.getClientConfig, 'headerColorOverride', 'ffffff');
    },

    isCogAvailable() {
      return (
        this.hasLocaleSwitcherEnabled ||
        this.hasTenantsSwitcherEnabled ||
        this.hasFormatsSwitcherEnabled
      );
    },

    hasLocaleSwitcherEnabled() {
      return get(this.getClientConfig, 'features.localeSwitcherEnabled', false);
    },

    hasTenantsSwitcherEnabled() {
      return this.getAvailableTenants.length > 0;
    },

    hasFormatsSwitcherEnabled() {
      return get(this.getClientConfig, 'features.formatsSwitcherEnabled', false);
    },

    hasBrandLogoEnabled() {
      return get(this.getClientConfig, 'features.brandLogoEnabled', false);
    },

    routes() {
      return this.$router.options.routes
        .filter(route => {
          if (get(route.meta, 'requiresPermission'))
            return this.hasPermission(route.meta.requiresPermission) && !route.omitFromNav;
          return !route.omitFromNav;
        })
        .filter(route => {
          // Only show admin page on whitelisted environments
          // The admin BE is also excluded based on the environments.
          if (route.name === 'AdminPage') return this.getShowAdminControlsPermissions;
          if (route.name === 'ClientAdminPage') return this.getShowClientAdminControlsPermissions;
          if (route.name === 'ReportingPage') return this.getShowReportingControlsPermissions;
          if (route.name === 'ReportingPowerBIPage') return this.getShowReportingPowerBIPermissions;
          if (route.name === 'CompoundExtractsPage') return this.getShowCompoundExtractsPermissions;
          if (route.name === 'TemplatesPage') return this.getShowTemplatesPermissions;
          if (route.name === 'BundlesPage') return this.getShowBundlesPermissions;
          return true;
        })
        .sort((a, b) => {
          if (Number.isFinite(a.index) && Number.isFinite(b.index)) return a.index - b.index;
          if (Number.isFinite(a.index)) return -1;
          if (Number.isFinite(b.index)) return 1;

          return (b.name || '').localeCompare(a.name || '');
        });
    },

    fullToolGuideEnabled() {
      return get(this.getClientConfig, 'features.fullToolGuideEnabled', false);
    },

    docsButtonVisible() {
      return this.canViewDocs && this.fullToolGuideEnabled;
    },
  },

  methods: {
    ...mapActions(useContextStore, ['logout', 'resetState']),

    onLogout() {
      return this.logout().then(
        redirectUrl => {
          this.$router.push(redirectUrl);
          this.resetState();
        },
        () => {
          // eslint-disable-next-line no-console
          console.log('Logout error!');
        }
      );
    },

    getActiveRoute(route) {
      return this.$route.fullPath.includes(route.path);
    },
  },
};
</script>

<style scoped lang="scss">
@import '@style/base/_variables.scss';

.assortment-toolbar {
  z-index: 199 !important;
  background-color: $assortment-header-footer-colour;
  box-shadow:
    0px 1px 3px -1px rgba(0, 0, 0, 0.05),
    0px 2px 2px 0px rgba(0, 0, 0, 0.05),
    0px 1px 9px 5px rgba(0, 0, 0, 0.05) !important;

  .v-toolbar__items {
    display: flex;
    align-items: center;
    .v-btn {
      font-size: 1.4rem !important;
    }
  }

  :deep(.v-toolbar__content) {
    padding: 4px 28px;
  }
}

.home-link {
  color: $assortment-secondary-colour;
}
.home-link:hover {
  text-decoration: none;
}

.nav-link:hover {
  text-decoration: none;
}

.nav-link {
  color: $assortment-primary-colour;
  border-radius: 0%;

  :deep(.v-btn__content) {
    font-weight: normal !important;
  }
}

.active-link {
  border-bottom: 0.5rem solid $assortment-primary-colour;
  padding-bottom: 0rem;
}

.v-btn {
  font-size: 1.5rem;
}

.workpackage-selection {
  position: fixed;
  background-color: white;
  width: 100%;
  padding: 3rem 3rem 1rem;
  z-index: 90;
  color: #4a4a4a;
  font-size: 1.4rem;

  .description {
    font-weight: 600;
  }

  .dates {
    color: $assortment-text-colour;
    font-size: 1.2rem;
  }
}
.nav-menu-list-item {
  .v-list-item {
    min-height: 3.5rem;
    // padding-right: 0;
    padding: 0 0 0 16px;
  }

  :deep(.v-field__input) {
    padding: 0;
  }
}

// not working somehow
// .v-list-item {
//   min-height: 3.5rem;
//   // padding-right: 0;
//   padding: 0 0 0 16px;
// }

.assortment-toolbar.warning {
  background-color: $assortment-orange !important;
}

.theme--light.v-list {
  color: $sub-navigation-link;

  .v-list-item--link {
    color: $sub-navigation-link;
    height: 30px;
  }

  .v-list__tile {
    height: 3rem;
  }

  .v-list-item__title {
    font-size: 1.5rem;
  }

  .v-list-item--link:hover {
    background-color: $sub-navigation-hover;
    color: $sub-navigation-link;
  }
}

.v-menu__content {
  margin-top: 2.4rem;
}

.cog-icon {
  height: 1.6rem;
  margin-right: 3rem;

  &:hover {
    cursor: pointer;
  }
}

.brand-logo {
  &__container {
    max-width: 120px;

    .home-link {
      height: auto;
      min-width: auto;
      padding: 0;

      &:before {
        background: transparent;
      }
    }
  }

  &__img {
    height: auto;
    max-height: 40px;
  }
}

.v-toolbar-items {
  align-self: center;
  align-items: center;
}
</style>

<style lang="scss">
@import '@style/base/_variables.scss';

.options {
  min-width: 6rem !important;

  .v-list .v-list-item--active .v-list-item__content {
    color: $assortment-greyed-out-colour;
  }
}
</style>
